import { apiPostRequest } from '@/utils/apiClient';

/**
 * セッションIDを取得（存在しない場合は生成）
 */
export const getOrCreateSessionId = (): string => {
    const storageKey = 'kotomil_session_id';
    let sessionId = sessionStorage.getItem(storageKey);
    if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem(storageKey, sessionId);
    }
    return sessionId;
};

/**
 * カスタム分析イベントログの結果情報
 */
export interface CustomAnalysisResult {
    result: 'success' | 'empty' | 'error';
    resultCount: number;
    dateRangeDays: number;
    execTimeMs: number;
    errorCode: string | null;
    errorMessage?: string | null;
    actualCount?: number | null;
}

/**
 * カスタム分析イベントログを送信
 */
export const logCustomAnalysisEvent = async (
    startTime: string,
    endTime: string,
    searchKeyword: string,
    searchColor: { value: string; label: string },
    analysisResult: CustomAnalysisResult,
): Promise<void> => {
    try {
        const sessionId = getOrCreateSessionId();

        await apiPostRequest('/api/log/event', {
            sessionId,
            eventType: 'function_use',
            eventKey: 'custom_analysis',
            metadata: {
                // 先頭に追加するメタデータ
                analysis_result: analysisResult.result,
                result_count: analysisResult.resultCount,
                date_range_days: analysisResult.dateRangeDays,
                exec_time_ms: analysisResult.execTimeMs,
                error_code: analysisResult.errorCode,
                error_message: analysisResult.errorMessage ?? null,
                actual_count: analysisResult.actualCount ?? null,
                // 既存のメタデータ
                period: {
                    startDate: startTime,
                    endDate: endTime,
                },
                color: searchColor?.value ?? '',
                hasKeyword: Boolean(searchKeyword),
            },
        });
    } catch (error) {
        // ログ記録失敗はユーザー体験に影響させない
        console.error('カスタム分析イベントログの送信に失敗しました', error);
    }
};

/**
 * アクティビティビューイベントログのメタデータ
 */
export interface ActivityViewMetadata {
    // 3項目分の表示名の有無
    hasDisplayName1: boolean;
    hasDisplayName2: boolean;
    hasDisplayName3: boolean;
    // 検索キーワードの有無
    hasKeyword1: boolean;
    hasKeyword2: boolean;
    hasKeyword3: boolean;
    // 色情報
    color1: string;
    color2: string;
    color3: string;
}

/**
 * アクティビティビューイベントログを送信
 */
export const logActivityViewEvent = async (
    metadata: ActivityViewMetadata,
): Promise<void> => {
    try {
        const sessionId = getOrCreateSessionId();

        await apiPostRequest('/api/log/event', {
            sessionId,
            eventType: 'function_use',
            eventKey: 'activity_view_submit',
            metadata: {
                has_display_name_1: metadata.hasDisplayName1,
                has_display_name_2: metadata.hasDisplayName2,
                has_display_name_3: metadata.hasDisplayName3,
                has_keyword_1: metadata.hasKeyword1,
                has_keyword_2: metadata.hasKeyword2,
                has_keyword_3: metadata.hasKeyword3,
                color_1: metadata.color1 || '',
                color_2: metadata.color2 || '',
                color_3: metadata.color3 || '',
            },
        });
    } catch (error) {
        // ログ記録失敗はユーザー体験に影響させない
        console.error('アクティビティビューイベントログの送信に失敗しました', error);
    }
};

/**
 * シンプル分析イベントログのメタデータ
 */
export interface SimpleAnalysisMetadata {
    // 設定した期間
    periodType: string;
    // 色情報
    color: string;
    // キーワードの有無
    hasKeyword: boolean;
}

/**
 * シンプル分析イベントログを送信
 */
export const logSimpleAnalysisEvent = async (
    metadata: SimpleAnalysisMetadata,
): Promise<void> => {
    try {
        const sessionId = getOrCreateSessionId();

        await apiPostRequest('/api/log/event', {
            sessionId,
            eventType: 'function_use',
            eventKey: 'simple_analysis_submit',
            metadata: {
                period_type: metadata.periodType,
                color: metadata.color || '',
                has_keyword: metadata.hasKeyword,
            },
        });
    } catch (error) {
        // ログ記録失敗はユーザー体験に影響させない
        console.error('シンプル分析イベントログの送信に失敗しました', error);
    }
};

